<template>
  <app-card :status="applicationStatus.key" class="flex flex-col">
    <div class="h-full mb-4 p-4">
      <app-badge
        :icon="applicationStatus.icon"
        :type="applicationStatus.messageType"
      >
        {{ applicationStatus.message }}
      </app-badge>
      <h3 class="mb-0">
        {{ applicationTitle }}
      </h3>
      <p
        v-if="application.hasOwnProperty('competition')"
        class="text-grey-600 mt-0"
      >
        <small>{{ application.competition.title }}</small>
      </p>
      <p
        v-if="applicationStatus.key == 'extended'"
        class="text-neutral-600 mb-0"
      >
        <small
          >Deadline extended until
          {{
            this.format(this.application_extended_until, "do MMMM yyyy")
          }}</small
        >
      </p>
      <div class="text-xs mt-2 text-grey-500">
        <p v-if="!submitted_date">
          <icon icon="refresh" class="w-4 h-4 icons-grey align-middle" />
          Last updated on {{ updated_at }}
        </p>
        <p v-else>
          <icon icon="refresh" class="w-4 h-4 icons-grey align-middle" />
          Completed on {{ updated_at }}
        </p>
      </div>
    </div>
    <div class="border-t bg-grey-100 p-4 text-right">
      <icon-link
        :href="applicationLink"
        icon="arrow-thin-right-circle"
        icon-placement="right"
      >
        {{ applicationStatus.link }}
      </icon-link>
    </div>
  </app-card>
</template>

<script>
import { format, isBefore, parseISO } from "date-fns";
export default {
  name: "UserApplicationCard",
  props: {
    application: {
      type: Object,
      required: true
    }
  },
  computed: {
    applicationTitle() {
      if (this.application.title) {
        return `[${this.application.ref}] ${this.application.title}`;
      } else {
        return this.application.ref;
      }
    },
    applicationStatus() {
      const status =
        this.application.rejected_at !== null
          ? "rejected"
          : this.application.withdrawn_at !== null
          ? "withdrawn"
          : this.application.submitted_at !== null
          ? "complete"
          : this.application.submitted_at && !this.hasPassed
          ? "review"
          : !this.application.submitted_at && this.hasPassed
          ? "passed"
          : this.application.application_extended_until !== null
          ? "extended"
          : "draft";
      return this.statusLookup(status);
    },
    applicationLink() {
      return route("applications.feedback.view", {
        application: this.application.uuid
      });
    },
    now() {
      return new Date();
    },

    hasPassed() {
      if (this.application.application_extended_until !== null) {
        return isBefore(this.application_extended_until, this.now);
      }
      return isBefore(this.application_end_date, this.now);
    },

    updated_at() {
      if (!this.application.updated_at) return;
      return format(
        parseISO(this.application.updated_at),
        "do MMMM yyyy (h:mma)"
      );
    },

    submitted_at() {
      if (!this.application.submitted_at) return;
      return format(parseISO(this.application.submitted_at), "do MMMM yyyy");
    },

    submitted_date() {
      if (!this.application.submitted_at) return;
      return parseISO(this.application.submitted_at);
    },

    application_end_date() {
      return parseISO(this.application.competition.application_end_date);
    },

    application_extended_until() {
      if (!this.application.application_extended_until) return;
      return parseISO(this.application.application_extended_until);
    }
  },
  methods: {
    format,
    statusLookup(status) {
      const statuses = {};
      statuses.complete = {
        key: "complete",
        messageType: "positive",
        message: "Submitted",
        icon: "check",
        link: "Review application"
      };
      statuses.award = {
        key: "complete",
        messageType: "positive",
        message: "Awarded",
        icon: "star",
        link: "Review application"
      };
      statuses.review = {
        key: "review",
        messageType: "positive",
        message: "Under Review",
        icon: "time",
        link: "Review application"
      };
      statuses.passed = {
        key: "passed",
        messageType: "grey",
        message: "Expired",
        icon: "close-circle",
        link: "View application"
      };
      statuses.withdrawn = {
        key: "withdrawn",
        messageType: "grey",
        message: "Withdrawn",
        icon: "close-circle",
        link: "Review application"
      };
      statuses.rejected = {
        key: "rejected",
        messageType: "negative",
        message: "Rejected",
        icon: "close-circle",
        link: "Review feedback"
      };
      statuses.draft = {
        key: "draft",
        messageType: "neutral",
        message: "Draft",
        icon: "time",
        link: "Continue application"
      };
      statuses.extended = {
        key: "extended",
        messageType: "neutral",
        message: "Deadline Extended",
        icon: "time",
        link: "Continue application"
      };
      statuses.default = {
        key: null,
        messageType: null,
        message: null,
        icon: null,
        link: "View application"
      };
      return statuses.hasOwnProperty(status)
        ? statuses[status]
        : statuses["default"];
    }
  }
};
</script>

<style></style>
